import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import Toast, { ToastVariant } from '../components/ui/Toast';

interface ToastState {
  id: number;
  message: string;
  variant: ToastVariant;
  onUndo?: () => void;
  duration?: number; // Optional custom duration
}

interface ToastContextType {
  showToast: (message: string, variant?: ToastVariant) => void;
  showToastWithUndo: (message: string, onUndo: () => void, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

let toastId = 0;

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastState[]>([]);
  const MAX_TOASTS = 5; // Limit number of toasts displayed simultaneously (standard practice)

  // Standard SaaS durations: success/info: 3-5s, warning/error: 5-7s
  const getDefaultDuration = (variant: ToastVariant): number => {
    switch (variant) {
      case 'success':
      case 'info':
        return 4000; // 4 seconds (middle of 3-5s range)
      case 'warning':
      case 'error':
        return 6000; // 6 seconds (middle of 5-7s range)
      default:
        return 5000; // 5 seconds default
    }
  };

  const showToast = useCallback((message: string, variant: ToastVariant = 'info') => {
    const id = toastId++;
    const duration = getDefaultDuration(variant);
    setToasts((prev) => {
      // Keep only the most recent toasts (standard: limit to prevent UI clutter)
      const newToasts = [...prev, { id, message, variant, duration }];
      return newToasts.slice(-MAX_TOASTS);
    });
  }, []);

  const showToastWithUndo = useCallback((message: string, onUndo: () => void, variant: ToastVariant = 'info') => {
    const id = toastId++;
    // Standard SaaS: Toasts with Undo auto-dismiss after 5-7 seconds (6 seconds default)
    // User can click Undo before it disappears, or let it disappear to confirm deletion
    const duration = 6000; // 6 seconds (middle of 5-7s range)
    setToasts((prev) => {
      // Keep only the most recent toasts (standard: limit to prevent UI clutter)
      const newToasts = [...prev, { id, message, variant, onUndo, duration }];
      return newToasts.slice(-MAX_TOASTS);
    });
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, showToastWithUndo }}>
      {children}
      {/* Standard SaaS: Single toast container, positioned top-right, with stacking */}
      <div className="fixed top-4 right-4 z-50 space-y-2 pointer-events-none">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast
              message={toast.message}
              variant={toast.variant}
              duration={toast.duration}
              onClose={() => removeToast(toast.id)}
              onUndo={toast.onUndo}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

